/**
 * API module that dispatches methods for `optimizely.push([methodName, arg1, arg2])`
 * @module
 */
var _ = require('optimizely-client-fn');
var Logger = require('core/utils/logger');

var coreModules = require('./core_modules');

var di = require('core/di');
var PluginStore = di.get('stores/plugins');
var actionTypes = require('core/constants/action_types');
var enums = require('core/constants/enums');
var Flux = require('core/flux');

var EXPERIMENTAL_EDGE_SPA = __EXPERIMENTAL_EDGE_SPA__;

/**
 * List of api versions
 * @private
 */
var apiVersions = [
  require('./legacy'),
  require('./v1'),
  require('./v2')
];

/**
 * Subset of API function calls that should be executed ASAP,
 * which cannot wait to be executed during the normal initializeApi call
 */
var highPriorityApiCalls = [
  'clientMetadata',
  'cookieDomain',
  'disable',
  'load',
  'optOut',
  // Need to make sure all RUM context data is initialized before RUM.queueBeacons is called
  'rum',
];

/**
 * Dispatches to globalFunctions
 * Usage (v1): push({type: 'event', 'version': 1, eventName: 'buy', tags: { revenue: 1.23 }})
 * version is optional, defaults to 1
 * Usage (legacy): push(['trackEvent', 'buy', { revenue: 1.23 }]), push('disable')
 * @param {Object|Array<Object>|string} argArrayOrObject
 * @param {boolean} highPriorityOnly
 *
 * @returns {boolean} True if the function was invoked, otherwise false
 */
exports.push = function(argArrayOrObject:any|Array<any>|string, highPriorityOnly:boolean): boolean {
  var methodName;
  var fn;
  var args;
  var version;

  if (!_.isArray(argArrayOrObject) && _.isObject(argArrayOrObject)) {
    // v1 or newer API
    version = !_.isUndefined(argArrayOrObject.version) ? argArrayOrObject.version : 1;
    methodName = argArrayOrObject.type;
    args = [argArrayOrObject];
  } else if(_.isArray(argArrayOrObject)) {
    // Legacy
    version = 0;
    // Extract method name from first element of array
    methodName = argArrayOrObject[0];
    // Extract args from rest of array
    args = argArrayOrObject.slice(1);
  } else if (_.isString(argArrayOrObject)) {
    // Legacy
    version = 0;
    // Method name is entire arg
    methodName = argArrayOrObject;
    args = [];
  } else {
    Logger.warn('API / Ignoring non-array/object/string argument:', argArrayOrObject);
    return false;
  }

  if (apiVersions[version]) {
    fn = apiVersions[version][methodName];
  }

  if (highPriorityOnly) {
    if (highPriorityApiCalls.indexOf(methodName) === -1) {
      Logger.debug('API / Ignoring non high priority function:', methodName, args);
      return false;
    }
  }

  if (!fn) {
    Logger.warn(
      'API / No function found for "' + methodName + '" (v' + version + ') with arguments:', args);
    return false;
  }

  Logger.log('API / Executing: "' + methodName, '" with arguments:', args);
  try {
    fn.apply(null, args);
    Flux.dispatch(actionTypes.RECORD_API_USAGE, {
      // 0 AKA legacy: omit version (to align with classic reporting)
      methodName: version ? 'v' + version + '.' + methodName : methodName,
    });
  } catch(err) {
    Logger.error(err);
  }
  return true;
};

/**
 * Retrieve requested API module.
 * Usage: get('data');
 * @param {string} name
 * @returns {*}
 */
exports.get = function(name:string): any {
  if (EXPERIMENTAL_EDGE_SPA && name !== 'state') {
    // Expose only the get('state') API for Edge SPA follow-on snippets
    Logger.warn('Module "' + name + '" not found.');
    return;
  }

  Logger.log('API / Getting module: "' + name + '"');
  var apiModule = coreModules[name];
  if (apiModule) {
    if (_.isArray(apiModule)) {
      // The module is a getter; use di.
      apiModule = di.evaluate(apiModule);
    }
  } else {
    apiModule = PluginStore.getPlugin(enums.PluginTypes.apiModules, name);
  }

  if (apiModule) {
    Flux.dispatch(actionTypes.RECORD_API_USAGE, {
      methodName: 'get.' + name,
    });
    return apiModule;
  }

  Logger.warn('Module "' + name + '" not found.');
};


if (__TEST__) {
  exports.versions = apiVersions;

  exports.setEdgeSpa = function(edgeSpa) {
    EXPERIMENTAL_EDGE_SPA = edgeSpa;
  };
}
